import { useEffect, useState } from "react";
import { api, type GovernanceSummary, type TenantsSummary, type TodaySummary } from "../lib/api";

interface Stats {
  governance: GovernanceSummary | null;
  tenants: TenantsSummary | null;
  workItems: { by_status: Array<{ status: string; n: number }> } | null;
  crossDept: { by_status: Array<{ status: string; n: number }> } | null;
  approvals: { by_status: Array<{ status: string; n: number }> } | null;
  loading: boolean;
  error: string | null;
}

export default function Cockpit() {
  const [s, setS] = useState<Stats>({
    governance: null, tenants: null, workItems: null, crossDept: null, approvals: null,
    loading: true, error: null,
  });
  const [today, setToday] = useState<TodaySummary | null>(null);
  const [todayWindow, setTodayWindow] = useState<number>(24);
  const [todayLoading, setTodayLoading] = useState<boolean>(true);
  const [todayError, setTodayError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api<GovernanceSummary>("/governance/summary"),
      api<TenantsSummary>("/tenants/_summary"),
      api<{ by_status: Array<{ status: string; n: number }> }>("/work-items/_summary"),
      api<{ by_status: Array<{ status: string; n: number }> }>("/cross-dept/_summary"),
      api<{ by_status: Array<{ status: string; n: number }> }>("/approvals/_summary"),
    ])
      .then(([governance, tenants, workItems, crossDept, approvals]) =>
        setS({ governance, tenants, workItems, crossDept, approvals, loading: false, error: null })
      )
      .catch((e) => setS((prev) => ({ ...prev, loading: false, error: e.message })));
  }, []);

  useEffect(() => {
    setTodayLoading(true);
    setTodayError(null);
    api<TodaySummary>(`/today-summary?hours=${todayWindow}`)
      .then((data) => {
        setToday(data);
        setTodayLoading(false);
      })
      .catch((e) => {
        setTodayError(e.message);
        setTodayLoading(false);
      });
  }, [todayWindow]);

  if (s.loading) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  if (s.error) return <div className="text-center py-12 text-red-600">Error: {s.error}</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Cockpit</h1>
        <p className="text-sm text-gray-500 mt-1">
          Multi-tenant ops dashboard · Phase B Hafta 3 skeleton
        </p>
      </div>

      <TodaySection
        data={today}
        loading={todayLoading}
        error={todayError}
        window={todayWindow}
        onWindowChange={setTodayWindow}
      />

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Governance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card label="Rules" value={s.governance?.rules} />
          <Card label="Agents" value={s.governance?.agents} />
          <Card label="Skills" value={s.governance?.skills} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Tenants</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card label="Active" value={s.tenants?.active_total?.total} />
          <Card label="Active MRR" value={`$${s.tenants?.active_total?.mrr_total_usd ?? 0}`} />
          <Card label="External" value={s.tenants?.by_type?.find((x) => x.type === "external")?.n} />
          <Card label="Internal-origin" value={s.tenants?.by_type?.find((x) => x.type === "internal_origin")?.n} />
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <DistributionCard title="Work Items" data={s.workItems?.by_status} />
        <DistributionCard title="Cross-Dept Reqs" data={s.crossDept?.by_status} />
        <DistributionCard title="Approvals" data={s.approvals?.by_status} />
      </section>

      <OpsSection />

      <AuditFeedSection />
    </div>
  );
}

interface AuditEvent {
  id: string;
  tenant_id: string;
  user_id: string | null;
  agent_name: string | null;
  event_type: string;
  resource_type: string | null;
  resource_id: string | null;
  reason: string | null;
  occurred_at: string;
}

function AuditFeedSection() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [summary, setSummary] = useState<{ by_event_type: Array<{ event_type: string; n: number }>; total: number } | null>(null);
  const [hours, setHours] = useState<number>(1);
  const [eventTypeFilter, setEventTypeFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ hours: String(hours), limit: "30" });
    if (eventTypeFilter) params.set("event_type", eventTypeFilter);
    Promise.all([
      api<{ events: AuditEvent[] }>(`/audit/feed?${params.toString()}`),
      api<{ by_event_type: Array<{ event_type: string; n: number }>; total: number }>(`/audit/_summary?hours=${hours}`),
    ])
      .then(([feed, sum]) => {
        setEvents(feed.events ?? []);
        setSummary(sum);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [hours, eventTypeFilter]);

  function emojiForType(t: string): string {
    if (t.startsWith("cron_")) return "⏰";
    if (t.startsWith("tool_action_")) return "🛂";
    if (t.startsWith("backup_")) return "💾";
    if (t.startsWith("d1_drift")) return "⚠";
    if (t === "create") return "➕";
    if (t === "update") return "✏";
    if (t === "state_change") return "🔄";
    if (t === "credit_grant_set") return "💳";
    return "·";
  }

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return `${Math.round(ms/1000)}s`;
    if (ms < 3600_000) return `${Math.round(ms/60_000)}m`;
    if (ms < 86400_000) return `${Math.round(ms/3600_000)}h`;
    return `${Math.round(ms/86400_000)}d`;
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500">
          Activity Feed
          {summary && <span className="ml-2 text-xs text-gray-400 normal-case font-normal">{summary.total} event in {hours}h</span>}
        </h2>
        <div className="flex gap-1 text-xs">
          {[1, 6, 24, 168].map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`px-2 py-1 rounded border ${
                hours === h
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
            >
              {h === 1 ? "1h" : h === 6 ? "6h" : h === 24 ? "24h" : "1w"}
            </button>
          ))}
        </div>
      </div>

      {/* Type filter chips */}
      {summary && summary.by_event_type.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 text-xs">
          <button
            onClick={() => setEventTypeFilter(null)}
            className={`px-2 py-0.5 rounded border ${
              eventTypeFilter === null
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
            }`}
          >
            all
          </button>
          {summary.by_event_type.slice(0, 10).map((t) => (
            <button
              key={t.event_type}
              onClick={() => setEventTypeFilter(t.event_type)}
              className={`px-2 py-0.5 rounded border ${
                eventTypeFilter === t.event_type
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
            >
              {emojiForType(t.event_type)} {t.event_type} <span className="opacity-60">×{t.n}</span>
            </button>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && <div className="p-4 text-sm text-gray-400">Loading…</div>}
        {!loading && events.length === 0 && <div className="p-4 text-sm text-gray-400">No events in window</div>}
        {!loading && events.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {events.map((e) => (
              <li key={e.id} className="px-3 py-2 text-xs flex items-start gap-3 hover:bg-gray-50">
                <span className="text-base leading-none mt-0.5">{emojiForType(e.event_type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-gray-900">{e.event_type}</span>
                    <span className="text-gray-500">{e.tenant_id}</span>
                    {e.agent_name && <span className="text-gray-400 text-[10px]">via {e.agent_name}</span>}
                    <span className="text-gray-400 ml-auto tabular-nums shrink-0">{timeAgo(e.occurred_at)}</span>
                  </div>
                  {e.reason && <div className="text-gray-600 truncate mt-0.5">{e.reason}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

interface BriefSummary {
  id: string;
  brief_type: string;
  cadence_period: string;
  title: string;
  health_indicator: string | null;
  health_score: number | null;
  created_at: string;
}

interface DriftFinding24h {
  finding_kind: string;
  occurrence_count: number;
  last_seen: string;
}

interface BackupVerify {
  ok: boolean;
  d1_check: { ok: boolean; rows_in_tenants: number };
  r2_check: { ok: boolean; recent_snapshot_days: string[]; days_with_recent_data: number; gap_warning: boolean };
  phase_g_30day_progress: { days_logged: number; gap_count: number; ready: boolean };
}

interface PendingActionSummary {
  id: string;
  tool_name: string;
  proposed_action_summary: string;
  status: string;
  proposed_at: string;
  expires_at: string;
  time_remaining_minutes?: number;
  tenant_id: string;
}

function OpsSection() {
  const [briefs, setBriefs] = useState<BriefSummary[]>([]);
  const [drift, setDrift] = useState<DriftFinding24h[]>([]);
  const [backupVerify, setBackupVerify] = useState<BackupVerify | null>(null);
  const [pendingActions, setPendingActions] = useState<PendingActionSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeBriefType, setActiveBriefType] = useState<string | null>(null);
  const [activeBrief, setActiveBrief] = useState<any>(null);
  const [briefLoading, setBriefLoading] = useState<boolean>(false);
  const [decidingId, setDecidingId] = useState<string | null>(null);

  function refreshAll() {
    Promise.allSettled([
      api<{ briefs: BriefSummary[] }>("/exec-briefs/list?limit=20"),
      api<{ findings: DriftFinding24h[] }>("/drift/findings/24h"),
      api<BackupVerify>("/backups/verify"),
      api<{ actions: PendingActionSummary[] }>("/tool-actions/pending"),
    ]).then((results) => {
      if (results[0].status === "fulfilled") setBriefs(results[0].value.briefs ?? []);
      if (results[1].status === "fulfilled") setDrift(results[1].value.findings ?? []);
      if (results[2].status === "fulfilled") setBackupVerify(results[2].value);
      if (results[3].status === "fulfilled") setPendingActions(results[3].value.actions ?? []);
      setLoading(false);
    });
  }

  useEffect(() => {
    refreshAll();
  }, []);

  // Phase E1 realtime — subscribe to _global channel for ops-wide events
  // (briefs publish, drift detected, etc.). Tenant-specific channels not
  // surfaced here — Cockpit shows aggregate views only.
  useEffect(() => {
    const apiBase = (import.meta as any).env?.VITE_API_BASE || "/api";
    const url = `${apiBase}/realtime/_global?channels=briefs,tool_actions`;
    const es = new EventSource(url);
    es.addEventListener("msg", () => {
      refreshAll();
    });
    return () => { es.close(); };
  }, []);

  async function openBrief(briefType: string) {
    setActiveBriefType(briefType);
    setActiveBrief(null);
    setBriefLoading(true);
    try {
      const data = await api<{ brief: any }>(`/exec-briefs/latest/${briefType}`);
      setActiveBrief(data.brief);
    } catch (e: any) {
      setActiveBrief({ error: e?.message ?? "load failed" });
    } finally {
      setBriefLoading(false);
    }
  }

  async function decidePending(id: string, decision: "approve" | "reject") {
    setDecidingId(id);
    try {
      await api(`/tool-actions/${id}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      });
      refreshAll();
    } catch (e) {
      console.error("decide failed:", e);
    } finally {
      setDecidingId(null);
    }
  }

  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">
        Ops · Phase F+G+H Live
      </h2>
      {loading && <div className="text-sm text-gray-400">Loading ops surface…</div>}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <BriefsCard briefs={briefs} onClickType={openBrief} activeType={activeBriefType} />
          <DriftCard findings={drift} />
          <BackupsCard verify={backupVerify} />
          <PendingActionsCard actions={pendingActions} onDecide={decidePending} decidingId={decidingId} />
        </div>
      )}

      {activeBriefType && (
        <BriefDetailModal
          briefType={activeBriefType}
          brief={activeBrief}
          loading={briefLoading}
          onClose={() => { setActiveBriefType(null); setActiveBrief(null); }}
        />
      )}
    </section>
  );
}

function BriefDetailModal({ briefType, brief, loading, onClose }: {
  briefType: string;
  brief: any;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b border-gray-200 px-4 py-3">
          <h3 className="font-medium">{briefType}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-4 overflow-y-auto text-sm">
          {loading && <div className="text-gray-400">Loading…</div>}
          {!loading && brief?.error && <div className="text-red-600">Error: {brief.error}</div>}
          {!loading && brief && !brief.error && (
            <>
              <div className="mb-3 text-xs text-gray-500 flex gap-3">
                <span>{brief.cadence_period}</span>
                <span>·</span>
                <span>{brief.health_indicator} ({brief.health_score}/100)</span>
                <span>·</span>
                <span>{new Date(brief.created_at).toLocaleString()}</span>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-gray-800">{brief.body_md}</pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BriefsCard({ briefs, onClickType, activeType }: {
  briefs: BriefSummary[];
  onClickType: (briefType: string) => void;
  activeType: string | null;
}) {
  const latestByType = new Map<string, BriefSummary>();
  for (const b of briefs) {
    if (!latestByType.has(b.brief_type)) latestByType.set(b.brief_type, b);
  }
  const expected = ["coo_daily", "cfo_weekly_credit", "cfo_cost_review", "cgo_weekly", "cmo_weekly", "cso_weekly", "coo_weekly", "audit_weekly"];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Cadence Briefs</h3>
        <span className="text-xs text-gray-400">{latestByType.size}/{expected.length}</span>
      </div>
      <ul className="space-y-1.5 text-xs">
        {expected.map((bt) => {
          const b = latestByType.get(bt);
          if (!b) {
            return (
              <li key={bt} className="flex justify-between text-gray-400 px-1">
                <span>{bt}</span>
                <span>missing</span>
              </li>
            );
          }
          const dotColor = b.health_indicator === "green" ? "bg-green-500"
            : b.health_indicator === "yellow" ? "bg-yellow-500"
            : b.health_indicator === "red" ? "bg-red-500"
            : "bg-gray-400";
          const isActive = activeType === bt;
          return (
            <li key={bt}>
              <button
                onClick={() => onClickType(bt)}
                className={`w-full flex justify-between items-center gap-2 text-left px-1 py-0.5 rounded hover:bg-gray-50 ${isActive ? "bg-gray-100" : ""}`}
              >
                <span className="flex items-center gap-1.5 truncate">
                  <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                  <span className="text-gray-700 truncate">{bt}</span>
                </span>
                <span className="text-gray-500 tabular-nums">{b.health_score ?? "?"}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DriftCard({ findings }: { findings: DriftFinding24h[] }) {
  const total = findings.reduce((acc, f) => acc + (f.occurrence_count ?? 0), 0);
  const indicator = total === 0 ? "🟢 0 drift" : total < 5 ? `🟡 ${total} drift` : `🔴 ${total} drift`;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">D1 Drift (24h)</h3>
        <span className="text-xs text-gray-500">{indicator}</span>
      </div>
      {findings.length === 0 ? (
        <div className="text-xs text-gray-400">no findings</div>
      ) : (
        <ul className="space-y-1 text-xs">
          {findings.slice(0, 6).map((f) => (
            <li key={f.finding_kind} className="flex justify-between gap-2">
              <span className="text-gray-600 truncate">{f.finding_kind.replace(/_/g, " ")}</span>
              <span className="text-gray-500 tabular-nums">×{f.occurrence_count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BackupsCard({ verify }: { verify: BackupVerify | null }) {
  if (!verify) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-medium text-gray-700 mb-2">R2 Backups</h3>
        <div className="text-xs text-gray-400">no data</div>
      </div>
    );
  }
  const days = verify.r2_check.days_with_recent_data;
  const phaseG = verify.phase_g_30day_progress;
  const dotColor = verify.r2_check.gap_warning ? "text-yellow-600" : "text-green-600";
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">R2 Backups</h3>
        <span className={`text-xs ${dotColor}`}>{verify.ok ? "ok" : "gap"}</span>
      </div>
      <ul className="space-y-1 text-xs">
        <li className="flex justify-between"><span className="text-gray-600">D1 alive</span><span className="text-gray-900">{verify.d1_check.ok ? "✓" : "✗"} ({verify.d1_check.rows_in_tenants} tenants)</span></li>
        <li className="flex justify-between"><span className="text-gray-600">Last 7d coverage</span><span className="text-gray-900">{days}/7</span></li>
        <li className="flex justify-between"><span className="text-gray-600">Phase G soak</span><span className="text-gray-900">{phaseG.days_logged}/30</span></li>
        <li className="flex justify-between"><span className="text-gray-600">Phase G ready</span><span className={phaseG.ready ? "text-green-600" : "text-gray-400"}>{phaseG.ready ? "✓" : "—"}</span></li>
      </ul>
    </div>
  );
}

function PendingActionsCard({ actions, onDecide, decidingId }: {
  actions: PendingActionSummary[];
  onDecide: (id: string, decision: "approve" | "reject") => Promise<void>;
  decidingId: string | null;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Pending Tool Actions</h3>
        <span className="text-xs text-gray-400">{actions.length}</span>
      </div>
      {actions.length === 0 ? (
        <div className="text-xs text-gray-400">empty</div>
      ) : (
        <ul className="space-y-2 text-xs">
          {actions.slice(0, 4).map((a) => (
            <li key={a.id} className="border-l-2 border-yellow-400 pl-2">
              <div className="text-gray-900 truncate font-medium">{a.tool_name}</div>
              <div className="text-gray-600 truncate">{a.proposed_action_summary}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-gray-400">
                  {a.tenant_id} · {a.time_remaining_minutes ?? "?"}m
                </span>
                <span className="flex gap-1">
                  <button
                    onClick={() => onDecide(a.id, "approve")}
                    disabled={decidingId !== null}
                    className="px-1.5 py-0 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded disabled:opacity-50"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => onDecide(a.id, "reject")}
                    disabled={decidingId !== null}
                    className="px-1.5 py-0 bg-red-600 hover:bg-red-700 text-white text-[10px] rounded disabled:opacity-50"
                  >
                    ✕
                  </button>
                </span>
              </div>
            </li>
          ))}
          {actions.length > 4 && <li className="text-gray-400">+{actions.length - 4} more</li>}
        </ul>
      )}
    </div>
  );
}

function TodaySection({
  data, loading, error, window, onWindowChange,
}: {
  data: TodaySummary | null;
  loading: boolean;
  error: string | null;
  window: number;
  onWindowChange: (h: number) => void;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500">
          Bugün — Son {window === 24 ? "24 saat" : window === 48 ? "48 saat" : "1 hafta"}
        </h2>
        <div className="flex gap-1 text-xs">
          {[24, 48, 168].map((h) => (
            <button
              key={h}
              onClick={() => onWindowChange(h)}
              className={`px-2 py-1 rounded border ${
                window === h
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
            >
              {h === 24 ? "24h" : h === 48 ? "48h" : "1w"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-gray-400">Loading…</div>}
      {error && <div className="text-sm text-red-600">Error: {error}</div>}
      {data && !loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PendingApprovalsCard items={data.pending_approvals} />
          <NewReqsCard items={data.new_cross_dept_requests} />
          <NewWorkItemsCard items={data.new_work_items} />
          <ActivityCard
            events={data.audit_event_counts}
            stateChanges={data.recent_state_changes}
            cronFires={data.cron_fires}
            briefs={data.briefs_published}
          />
        </div>
      )}
    </section>
  );
}

function PendingApprovalsCard({
  items,
}: {
  items: TodaySummary["pending_approvals"];
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Onay Bekliyor</h3>
        <span className="text-xs text-gray-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400">empty</div>
      ) : (
        <ul className="space-y-2 text-xs">
          {items.slice(0, 5).map((a) => (
            <li key={a.id} className="border-l-2 border-yellow-400 pl-2">
              <div className="flex justify-between gap-2">
                <span className="font-medium text-gray-900 truncate">{a.title}</span>
                <PriorityBadge p={a.priority} />
              </div>
              <div className="text-gray-500 mt-0.5">
                {a.tenant_id} · {a.department} · T{a.tier}
              </div>
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-gray-400 text-center pt-1">+{items.length - 5} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function NewReqsCard({
  items,
}: {
  items: TodaySummary["new_cross_dept_requests"];
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Yeni Cross-Dept</h3>
        <span className="text-xs text-gray-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400">empty</div>
      ) : (
        <ul className="space-y-2 text-xs">
          {items.slice(0, 5).map((r) => (
            <li key={r.id} className="border-l-2 border-blue-400 pl-2">
              <div className="flex justify-between gap-2">
                <span className="font-medium text-gray-900 truncate">{r.title}</span>
                <StatusBadge s={r.status} />
              </div>
              <div className="text-gray-500 mt-0.5">
                {r.tenant_id} · {r.requester_department} → {r.assignee_department}
              </div>
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-gray-400 text-center pt-1">+{items.length - 5} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function NewWorkItemsCard({
  items,
}: {
  items: TodaySummary["new_work_items"];
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Yeni Work Items</h3>
        <span className="text-xs text-gray-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400">empty</div>
      ) : (
        <ul className="space-y-2 text-xs">
          {items.slice(0, 5).map((w) => (
            <li key={w.id} className="border-l-2 border-emerald-400 pl-2">
              <div className="flex justify-between gap-2">
                <span className="font-medium text-gray-900 truncate">{w.title}</span>
                <PriorityBadge p={w.priority} />
              </div>
              <div className="text-gray-500 mt-0.5">
                {w.tenant_id} · {w.source_layer}
                {w.owner_department ? ` · ${w.owner_department}` : ""}
              </div>
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-gray-400 text-center pt-1">+{items.length - 5} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function ActivityCard({
  events,
  stateChanges,
  cronFires,
  briefs,
}: {
  events: TodaySummary["audit_event_counts"];
  stateChanges: TodaySummary["recent_state_changes"];
  cronFires: TodaySummary["cron_fires"];
  briefs: TodaySummary["briefs_published"];
}) {
  const totalEvents = events.reduce((sum, e) => sum + e.n, 0);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Activity</h3>
        <span className="text-xs text-gray-400">{totalEvents} events</span>
      </div>

      {events.length > 0 && (
        <div className="text-xs text-gray-600 mb-3">
          {events.slice(0, 4).map((e) => (
            <div key={e.event_type} className="flex justify-between">
              <span className="text-gray-500">{e.event_type}</span>
              <span className="font-medium">{e.n}</span>
            </div>
          ))}
        </div>
      )}

      {stateChanges.length > 0 && (
        <details className="mb-2">
          <summary className="text-xs font-medium text-gray-700 cursor-pointer">
            State changes ({stateChanges.length})
          </summary>
          <ul className="mt-1 space-y-1 text-xs text-gray-500">
            {stateChanges.slice(0, 5).map((c, i) => (
              <li key={i} className="truncate">
                {c.resource_type}/{c.resource_id}: {c.before_state ?? "—"} → {c.after_state ?? "—"}
              </li>
            ))}
          </ul>
        </details>
      )}

      {cronFires.length > 0 && (
        <details className="mb-2">
          <summary className="text-xs font-medium text-gray-700 cursor-pointer">
            Cron fires ({cronFires.length})
          </summary>
          <ul className="mt-1 space-y-1 text-xs text-gray-500">
            {cronFires.slice(0, 5).map((c, i) => (
              <li key={i} className="truncate">
                {c.skill_name} {c.after_state ? `· ${c.after_state}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}

      {briefs.length > 0 && (
        <details>
          <summary className="text-xs font-medium text-gray-700 cursor-pointer">
            Briefs ({briefs.length})
          </summary>
          <ul className="mt-1 space-y-1 text-xs text-gray-500">
            {briefs.slice(0, 5).map((b) => (
              <li key={b.id} className="truncate">
                {b.brief_type}: {b.title}
                {b.health_indicator ? ` · ${b.health_indicator}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}

      {totalEvents === 0 && stateChanges.length === 0 && cronFires.length === 0 && briefs.length === 0 && (
        <div className="text-xs text-gray-400">no activity</div>
      )}
    </div>
  );
}

function PriorityBadge({ p }: { p: string }) {
  const cls =
    p === "P0"
      ? "bg-red-100 text-red-700"
      : p === "P1"
      ? "bg-orange-100 text-orange-700"
      : "bg-gray-100 text-gray-600";
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{p}</span>;
}

function StatusBadge({ s }: { s: string }) {
  const cls =
    s === "approved" || s === "verified"
      ? "bg-green-100 text-green-700"
      : s === "pending_approval" || s === "peer_review"
      ? "bg-yellow-100 text-yellow-700"
      : s === "rejected" || s === "cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-gray-100 text-gray-600";
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{s}</span>;
}

function Card({ label, value }: { label: string; value: number | string | undefined }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-gray-900">{value ?? "—"}</div>
    </div>
  );
}

function DistributionCard({
  title, data,
}: { title: string; data: Array<{ status: string; n: number }> | undefined }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>
      {data && data.length > 0 ? (
        <ul className="space-y-1.5 text-sm">
          {data.map((row) => (
            <li key={row.status} className="flex justify-between">
              <span className="text-gray-600">{row.status}</span>
              <span className="font-medium text-gray-900">{row.n}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-gray-400">empty</div>
      )}
    </div>
  );
}
